import {
	HubConnectionBuilder,
	HubConnectionState,
	HubConnection,
	IHttpConnectionOptions,
	HttpTransportType,
} from "@microsoft/signalr";
import { StorageKeys } from "@/common/constants/storageKeys";
import { Variable, envVariable } from "@/common/constants/envVariable";
import { Command, RoomPrefix, ServerEvent } from "./types";
import { Lockr } from "@/common/Lockr";
import { HookAction } from "@/hooks/notification";
import { TokenManager } from "@/common/authentication";
import { captureError } from "../helpers/sentryHelper";

export class NotificationManager {
	private tokenManager: TokenManager = new TokenManager();
	private connectionOptions: IHttpConnectionOptions = {
		accessTokenFactory: async () => {
			try {
				return await this.tokenManager.getTokenOrRefresh();
			} catch (error: any) {
				captureError(error, "SignalR token acquisition failed");

				return "";
			}
		},
		transport: HttpTransportType.None,
	};

	private readonly socket: HubConnection = new HubConnectionBuilder()
		.withUrl(envVariable(Variable.SignalRUrl), this.connectionOptions)
		.withAutomaticReconnect([0, 1000, 3000, 5000])
		.build();

	// Single-slot callbacks dispatched by lifetime listeners registered once below.
	private onCloseCallback: (() => void) | null = null;
	private onReconnectingCallback: (() => void) | null = null;
	private onReconnectedCallback: (() => void) | null = null;

	private readonly joinedRooms: Set<string> = new Set<string>();

	constructor() {
		this.socket.onclose(() => this.onCloseCallback?.());
		this.socket.onreconnecting(() => this.onReconnectingCallback?.());
		this.socket.onreconnected(() => {
			this.joinedRooms.forEach((room: string) => this.joinRoom(room));
			this.onReconnectedCallback?.();
		});

		// Defensive rejoin: NotificationsHub.OnConnectedAsync sends MySocketConnected
		// on every connection (including transparent auto-reconnects). On normal
		// auto-reconnect both this handler and onreconnected fire, but the resulting
		// duplicate SubscribeToNotificationHub call is safe — the hub uses
		// Groups.AddToGroupAsync which is documented idempotent (same connection +
		// same group is a no-op). We keep this listener so that any server path
		// that re-emits MySocketConnected without SignalR transitioning through
		// onreconnecting/onreconnected (e.g. soft reset) still re-establishes rooms.
		this.socket.on(ServerEvent.SocketConnected, () => {
			this.joinedRooms.forEach((room: string) => this.joinRoom(room));
		});
	}

	public get isSocketConnected(): boolean {
		return this.socket.state === HubConnectionState.Connected;
	}

	public initialize = async () => {
		try {
			if (this.socket.state !== HubConnectionState.Disconnected) {
				console.warn(
					`SignalR is not disconnected, initialization called already, socketState: [${this.socket.state}]`
				);

				return;
			}

			const tokenId = Lockr.get(StorageKeys.TokenId);

			if (!tokenId) {
				console.warn(`SignalR initialization failed, token is missing`);
				this.onCloseCallback?.();
				return;
			}

			try {
				if (this.socket.state === HubConnectionState.Disconnected) {
					await this.socket.start();
				}
			} catch (error: any) {
				captureError(error, "SignalR initial connection failed");
				try {
					await this.tokenManager.tryRefreshToken();
					await this.socket.stop();
					await this.socket.start();
				} catch (error: any) {
					console.error(error);
					captureError(error, "SignalR connection failed after token refresh");
					this.onCloseCallback?.();
				}
			}
		} catch (err) {
			this.onCloseCallback?.();
			console.error(err);
		}
	};

	public disconnect() {
		this.socket.stop();
	}

	public subscribe<T>(event: ServerEvent, action: HookAction<T>): void {
		this.socket.on(event, action);
	}

	public unsubscribe<T>(event: ServerEvent, action: HookAction<T>): void {
		this.socket.off(event, action);
	}

	public onClose(callback: () => void): void {
		this.onCloseCallback = callback;
	}

	public onReconnecting(callback: () => void): void {
		this.onReconnectingCallback = callback;
	}

	public onReconnected(callback: () => void): void {
		this.onReconnectedCallback = callback;
	}

	public leaveDefaultRooms(): void {
		const tokenId = Lockr.get(StorageKeys.TokenId);

		if (!tokenId) {
			return;
		}

		this.leaveRoom(tokenId);

		const userId = Lockr.get(StorageKeys.UserId);

		if (!!userId) {
			this.leaveRoom(`${RoomPrefix.Default}.${userId}`);
		}

		const tenantId = Lockr.get(StorageKeys.TenantId);

		if (!!tenantId) {
			this.leaveRoom(`${RoomPrefix.TenantId}.${tenantId}`);
		}
	}

	public joinDefaultRooms(): void {
		if (this.socket.state !== HubConnectionState.Connected) {
			return;
		}

		const userId = Lockr.get(StorageKeys.UserId);

		if (!!userId) {
			this.joinRoom(`${RoomPrefix.Default}.${userId}`);
		}

		const tenantId = Lockr.get(StorageKeys.TenantId);

		if (!!tenantId) {
			this.joinRoom(`${RoomPrefix.TenantId}.${tenantId}`);
		}
	}

	public joinRoom(room: string): void {
		if (this.socket.state !== HubConnectionState.Connected) {
			return;
		}

		this.socket
			.invoke(Command.SubscribeToNotificationHub, room)
			.catch((err: Error) => console.error(err.toString()));

		if (!this.joinedRooms.has(room)) {
			this.joinedRooms.add(room);
		}
	}

	public leaveRoom(room: string): void {
		if (this.socket.state !== HubConnectionState.Connected) {
			return;
		}

		this.socket
			.invoke(Command.UnSubscribeToNotificationHub, room)
			.catch((err: Error) => console.error(err.toString()));

		this.joinedRooms.delete(room);
	}

	public notifyAllViewsAtLogin(userId: string, browserSessionId: string): void {
		if (this.socket.state !== HubConnectionState.Connected) {
			return;
		}

		this.socket
			.invoke(Command.NotifySignedInAnotherView, userId, browserSessionId)
			.catch((err: Error) => console.error(err.toString()));
	}
}
